'use client'

import type React from 'react'
import InlineSpinner from '@/components/Layout/InlineSpinner'
import ShippingPriceLabel from '@/components/Shipping/ShippingPriceLabel'
import type { ISelectShippingRequest, ISellerShippingQuote } from '~/schemas/shared/shipping'

interface ISellerShippingOptionsProps {
    sellers: ISellerShippingQuote[]
    /** Escolha em voo, ou `null`. */
    pending: ISelectShippingRequest | null
    onSelect: (sellerId: string, offerCode: string) => void
}

/** Provider legado (Maestro): sem opções, só o frete agregado do vendedor. */
const hasAggregateQuote = (seller: ISellerShippingQuote) => seller.price > 0 || Boolean(seller.days)

/**
 * Frete por vendedor (carrinho e checkout): as opções de cada vendedor, com a valendo marcada (`selectedOfferCode`, a
 * tela não guarda seleção própria). Durante uma escolha, a linha escolhida mostra o indicador no lugar do radio e as
 * outras ficam atenuadas e bloqueadas (sem o cinza nativo de `disabled` e sem `hover` preso), porque a API recota o
 * carrinho inteiro; o bloco fica com `aria-busy` e anuncia o recálculo.
 */
const SellerShippingOptions: React.FC<ISellerShippingOptionsProps> = ({ sellers, pending, onSelect }) => {
    const showSellerName = sellers.length > 1
    const busy = pending !== null

    return (
        <div aria-busy={busy}>
            <span role="status" aria-live="polite" className="sr-only">
                {busy ? 'Atualizando o frete, aguarde.' : ''}
            </span>

            {sellers.map((seller) => (
                <div key={seller.sellerId} className="py-2">
                    {showSellerName && (
                        <p className="px-3 py-1 text-sm font-bold text-darkgray font-sora">
                            Vendido por {seller.sellerName}
                        </p>
                    )}

                    {seller.shippingWasInvalidated && (
                        <p className="px-3 py-1 text-sm text-darkgray">
                            A escolha de frete anterior não vale mais para este CEP e estes itens. Selecionamos a mais
                            barata.
                        </p>
                    )}

                    {seller.options.length === 0 ? (
                        hasAggregateQuote(seller) ? (
                            <p className="px-3 py-2 max-md:text-sm text-md">
                                <ShippingPriceLabel price={seller.price} /> - {seller.days} dias úteis
                            </p>
                        ) : (
                            <p className="px-3 py-2 max-md:text-sm text-md text-darkgray">
                                Sem frete disponível para este vendedor.
                            </p>
                        )
                    ) : (
                        seller.options.map((option) => {
                            const id = `freight-${seller.sellerId}-${option.code}`
                            const optionIsPending =
                                pending?.sellerId === seller.sellerId && pending.offerCode === option.code

                            return (
                                <label
                                    key={option.code}
                                    htmlFor={id}
                                    className={`flex flex-col py-2 my-2 rounded-lg transition-opacity ${
                                        busy
                                            ? `cursor-default pointer-events-none ${optionIsPending ? '' : 'opacity-50'}`
                                            : 'cursor-pointer hover:bg-submenu-gray'
                                    }`}
                                >
                                    <div className="flex flex-row items-center gap-2 ml-2">
                                        {optionIsPending ? (
                                            <InlineSpinner className="text-harmonic-blue" />
                                        ) : (
                                            <input
                                                type="radio"
                                                id={id}
                                                name={`freight-${seller.sellerId}`}
                                                value={option.code}
                                                checked={option.code === seller.selectedOfferCode}
                                                aria-disabled={busy}
                                                onChange={() => {
                                                    if (busy) return
                                                    onSelect(seller.sellerId, option.code)
                                                }}
                                                className="w-4 h-4"
                                            />
                                        )}
                                        <span className="w-full max-md:text-sm text-md">
                                            {option.name} - <ShippingPriceLabel price={option.price} /> - {option.days}{' '}
                                            dias úteis
                                        </span>
                                    </div>
                                </label>
                            )
                        })
                    )}
                </div>
            ))}
        </div>
    )
}

export default SellerShippingOptions
